import { useCallback, useEffect, useRef } from 'react';

interface UseHorizontalDragResizeOptions {
  edge: 'left' | 'right';
  min: number;
  max: number;
  storageKey: string;
  width: number;
  onWidthChange: (width: number) => void;
  onResizingChange?: (resizing: boolean) => void;
  disabled?: boolean;
}

interface ActiveDragListeners {
  onMouseMove: (ev: MouseEvent) => void;
  onMouseUp: (ev: MouseEvent) => void;
}

const clampWidth = ({ min, max, value }: { min: number; max: number; value: number }): number =>
  Math.max(min, Math.min(max, value));

export const clampStoredWidth = ({
  key,
  min,
  max,
  fallback,
}: {
  key: string;
  min: number;
  max: number;
  fallback: number;
}): number => {
  const stored = localStorage.getItem(key);
  if (!stored) return fallback;
  const parsed = parseInt(stored, 10);
  return isNaN(parsed) ? fallback : clampWidth({ min, max, value: parsed });
};

export const useHorizontalDragResize = ({
  edge,
  min,
  max,
  storageKey,
  width,
  onWidthChange,
  onResizingChange,
  disabled,
}: UseHorizontalDragResizeOptions) => {
  const activeListenersRef = useRef<ActiveDragListeners | null>(null);

  useEffect(
    () => () => {
      if (activeListenersRef.current) {
        document.removeEventListener('mousemove', activeListenersRef.current.onMouseMove);
        document.removeEventListener('mouseup', activeListenersRef.current.onMouseUp);
        activeListenersRef.current = null;
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    [],
  );

  return useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = width;
      onResizingChange?.(true);

      const computeWidth = (clientX: number) => {
        const delta = edge === 'right' ? clientX - startX : startX - clientX;
        return clampWidth({ min, max, value: startWidth + delta });
      };

      const onMouseMove = (ev: MouseEvent) => {
        onWidthChange(computeWidth(ev.clientX));
      };

      const onMouseUp = (ev: MouseEvent) => {
        const finalWidth = computeWidth(ev.clientX);
        localStorage.setItem(storageKey, `${finalWidth}`);
        onResizingChange?.(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        activeListenersRef.current = null;
      };

      activeListenersRef.current = { onMouseMove, onMouseUp };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [edge, min, max, storageKey, width, onWidthChange, onResizingChange, disabled],
  );
};
